import { apiMutate, getApiErrorMessage, isApiError, parseApiErrorBody } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

// Mirrors the backend ModifyDatasetFilePrefixesRequest/Response contract for
// POST /api/catalog/v1/datasets/{id}/file-prefixes.
export interface DatasetFilePrefixRef {
  prefix: string;
}

export interface ModifyDatasetFilePrefixesRequest {
  add?: DatasetFilePrefixRef[];
  remove?: DatasetFilePrefixRef[];
}

export interface ModifiedDatasetFilePrefix {
  prefix: string;
  id: string;
}

export interface SkippedDatasetFilePrefix {
  prefix: string;
  reason: string;
  status_code: number;
}

export interface ModifyDatasetFilePrefixesResponse {
  errors: boolean;
  added: ModifiedDatasetFilePrefix[];
  removed: ModifiedDatasetFilePrefix[];
  skipped_additions: SkippedDatasetFilePrefix[];
  skipped_removals: SkippedDatasetFilePrefix[];
}

/**
 * Generic transport for the bulk file prefix association endpoint. Sends a delta
 * ({ add?, remove? }) and returns the outcome. Throws ApiError on a non-2xx
 * response (e.g. when every requested change failed).
 */
export const modifyDatasetFilePrefixes = (
  datasetId: string,
  request: ModifyDatasetFilePrefixesRequest
): Promise<ModifyDatasetFilePrefixesResponse> =>
  apiMutate<ModifyDatasetFilePrefixesResponse>(
    `${VITE_CATALOG_API}/v1/datasets/${datasetId}/file-prefixes`,
    "POST",
    request
  );

// When every requested change fails, the endpoint raises with
// `detail = { message, skipped_additions: [...], skipped_removals: [...] }` rather than
// the standard `{ detail: string }` envelope, so pull the per-prefix reasons out of it.
// Returns the distinct reasons joined into one message, or null if none are present.
const extractSkippedReasons = (
  body: Record<string, unknown> | null,
  field: "skipped_additions" | "skipped_removals"
): string | null => {
  const detail = body?.detail;
  if (!detail || typeof detail !== "object") {
    return null;
  }
  const skips = (detail as Record<string, unknown>)[field];
  if (!Array.isArray(skips)) {
    return null;
  }
  const reasons = skips
    .map((skip) => (skip as SkippedDatasetFilePrefix)?.reason)
    .filter((reason): reason is string => typeof reason === "string");
  const distinct = [...new Set(reasons)];
  return distinct.length ? distinct.join(" ") : null;
};

/**
 * Associate a single S3 key prefix with a dataset. Resolves on success, including the
 * idempotent case where the prefix was already associated. Throws an Error carrying the
 * API's human-readable reason when the addition fails.
 */
export const addDatasetFilePrefix = async (datasetId: string, prefix: string): Promise<void> => {
  let response: ModifyDatasetFilePrefixesResponse;
  try {
    response = await modifyDatasetFilePrefixes(datasetId, { add: [{ prefix }] });
  } catch (e) {
    if (isApiError(e)) {
      throw new Error(
        extractSkippedReasons(parseApiErrorBody(e), "skipped_additions") ?? getApiErrorMessage(e)
      );
    }
    throw e;
  }

  // A 2xx can still report a failed addition when a caller batches multiple prefixes (a
  // single-prefix addition never hits this: a total failure is thrown above). Surface it.
  const failure = response.skipped_additions.find((skip) => skip.status_code >= 400);
  if (failure) {
    throw new Error(failure.reason);
  }
};

/**
 * Disassociate a single S3 key prefix from a dataset. Resolves on success, including the
 * idempotent case where the prefix was not associated to begin with. Throws an Error
 * carrying the API's human-readable reason when the removal fails.
 */
export const removeDatasetFilePrefix = async (datasetId: string, prefix: string): Promise<void> => {
  let response: ModifyDatasetFilePrefixesResponse;
  try {
    response = await modifyDatasetFilePrefixes(datasetId, { remove: [{ prefix }] });
  } catch (e) {
    if (isApiError(e)) {
      throw new Error(
        extractSkippedReasons(parseApiErrorBody(e), "skipped_removals") ?? getApiErrorMessage(e)
      );
    }
    throw e;
  }

  // Same as additions: only reachable for a batched removal, but reported rather than
  // silently treated as a success.
  const failure = response.skipped_removals.find((skip) => skip.status_code >= 400);
  if (failure) {
    throw new Error(failure.reason);
  }
};
